import { useMemo } from "react";
import type { BaseEntity, PaginationState } from "../../UniversalCRMTable/types";
import type { AuthenticatedUser } from "@/lib/types/unified";
import { normalizeCustomFiltersToGraphQL } from "@/lib/services/filter-conversion";
import {
  buildPresetUserFilter,
  mergeFiltersWithAnd,
  resolveCurrentUserFilterTokens,
} from "@/lib/utils/crm/user-filter-preset";
import type { ExtendedGraphQLFilter, QueryParams } from "@/types/filter-types";
import {
  extractClientSideFilter,
  extractRpcFilter,
  removeExtendedProperties,
  isExtendedGraphQLFilter,
} from "@/types/filter-types";

interface UseQueryParamsProps<T extends BaseEntity> {
  filterContext: any;
  pagination: PaginationState;
  customFilters: Record<string, any>;
  filterPreset: "all" | "user";
  appUser: AuthenticatedUser;
  entityName: string;
  config: any;
  enableFTS: boolean;
}

/**
 * Builds GraphQL query parameters from filter context and pagination state
 * Handles: custom filters, filter context, search, pagination
 */
export function useQueryParams<T extends BaseEntity>({
  filterContext,
  pagination,
  customFilters,
  filterPreset,
  appUser,
  entityName,
  config,
  enableFTS,
}: UseQueryParamsProps<T>) {
  // CRITICAL: Include all filter-related dependencies in the dependency array
  // so that queryParams is recalculated when filters change (e.g., when switching
  // saved views via SavedViewsTabs). This ensures the GraphQL query is re-executed
  // with the new filters.
  //
  // NOTE: Do NOT include filterContext?.helpers in the dependency array as it
  // creates new function references on every render, causing infinite loops.
  // Instead, we call the helper functions directly when needed.
  const queryParams = useMemo(() => {
    const params: {
      first: number;
      after: string | null;
      filter: Record<string, any>;
      searchTerm?: string;
    } = {
      first: pagination.pageSize,
      after: pagination.cursor,
      filter: {},
    };

    // Always apply customFilters first (e.g. ticket_id for time entries tab) so linked-entity
    // tabs work even when filterContext is null (e.g. in modals or standalone usage).
    if (Object.keys(customFilters).length > 0) {
      Object.assign(params.filter, normalizeCustomFiltersToGraphQL(customFilters));
    }

    const withPresetUserFilter = (filter: Record<string, any>): Record<string, any> => {
      const resolvedFilter = resolveCurrentUserFilterTokens(filter, appUser?.id);

      if (filterPreset !== "user" || !appUser?.id) {
        return resolvedFilter;
      }

      const presetUserFilter = buildPresetUserFilter({
        entityName,
        userId: appUser.id,
      });

      return mergeFiltersWithAnd(resolvedFilter, presetUserFilter);
    };

    if (!filterContext) {
      return {
        ...params,
        filter: withPresetUserFilter(params.filter),
        searchTerm: undefined,
      };
    }

    // Add filters from FilterContext
    // The graphqlFilter already includes simple filters, advanced filters, and search
    if (Object.keys(filterContext.computed.graphqlFilter).length > 0) {
      const contextFilter = { ...filterContext.computed.graphqlFilter };

      // Handle search filter separately
      // Use the debounced search term from graphqlFilter._search, not the immediate filters.search
      const debouncedSearchTerm = contextFilter._search;
      if (debouncedSearchTerm) {
        delete contextFilter._search;

        // If FTS is enabled, skip GraphQL search filtering
        // FTS will handle search via the API endpoint instead
        if (!enableFTS) {
          const searchFilter = filterContext.helpers.constructSearchFilter(debouncedSearchTerm);

          // For deals, use full-text search via RPC function
          if (searchFilter._fullTextSearch) {
            params.searchTerm = searchFilter._fullTextSearch;
          } else {
            Object.assign(params.filter, searchFilter);
          }
        }
      }

      // Extract client-side filter if present (will be applied in the hook)
      const extendedFilter = contextFilter as ExtendedGraphQLFilter;
      
      if (isExtendedGraphQLFilter(extendedFilter)) {
        // Read BOTH extended properties before removing EITHER.
        // `removeExtendedProperties` deletes `_clientSideFilter` and `_rpcFilter`
        // together, so extracting the RPC filter after the client-side branch had
        // already called it always found nothing: a view carrying both silently
        // lost its server-side predicate and fell back to filtering in the
        // browser over whatever page happened to be loaded.
        const clientSideFilter = extractClientSideFilter(extendedFilter);
        const rpcFilter = extractRpcFilter(extendedFilter);

        if (clientSideFilter) {
          (params as QueryParams)._clientSideFilter = clientSideFilter;
        }
        if (rpcFilter) {
          (params as QueryParams)._rpcFilter = rpcFilter;
        }
        if (clientSideFilter || rpcFilter) {
          removeExtendedProperties(extendedFilter);
        }
      }

      // Add remaining filters
      Object.assign(params.filter, contextFilter);
    }

    return {
      ...params,
      filter: withPresetUserFilter(params.filter),
    };
  }, [
    filterContext?.computed.graphqlFilter,
    filterContext?.filters.search,
    filterContext?.filters.simple,
    filterContext?.filters.advanced,
    filterContext?.filters.activeViewId,
    pagination.pageSize,
    pagination.cursor,
    filterPreset,
    appUser?.id,
    customFilters,
    entityName,
    config,
    enableFTS,
  ]);

  return { queryParams };
}
